import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { colors, colorSystem } from '@/styles/theme'
import Text from '@/components/content/Text'
import SelectBox from '@/components/content/SelectBox'
import { useWaitVolumePrediction } from '@/services/outboundWaitVolume/useWaitVolumeQueries'
import { WAIT_VOLUME_SHIPPER_OPTIONS } from '@/services/outboundWaitVolume/constants'
import { CutTypeV2 } from '@/services/outboundSchedule2/types'
import { MAX_WAIT_TIME } from '@/pages/outbound/constants'
import { useFeatureFlag } from '@/services/feature/useFeatureQueries'
import { Utils } from '@/utils/Utils'
import { format, parseISO } from 'date-fns'

interface Props {
  cutType: CutTypeV2
}

// feature.usedBundle ON 시 MUSINSA_USED 는 MUSINSA 로 합포되어(BE 예측도 MUSINSA 로 합산) 화주 선택지에서 숨긴다.
const USED_BUNDLE_FLAG_KEY = 'feature.usedBundle'
const MUSINSA_SHIPPER_CODE = 'MUSINSA'
const MUSINSA_USED_SHIPPER_CODE = 'MUSINSA_USED'

function OutboundWaitingByTimeSlotPanel({ cutType }: Props) {
  const [shipperCode, setShipperCode] = useState<string>(MUSINSA_SHIPPER_CODE)

  // 조회 실패/로딩 중이면 false(OFF) → 기존대로 MUSINSA_USED 노출 (fail-safe).
  const { data: isUsedBundleEnabled } = useFeatureFlag(USED_BUNDLE_FLAG_KEY)

  // ON: MUSINSA_USED 제외 / OFF: 현행 그대로. (LOGISPRD-9649)
  const shipperOptions = useMemo(
    () =>
      isUsedBundleEnabled
        ? WAIT_VOLUME_SHIPPER_OPTIONS.filter((option) => option.value !== MUSINSA_USED_SHIPPER_CODE)
        : WAIT_VOLUME_SHIPPER_OPTIONS,
    [isUsedBundleEnabled],
  )

  // 플래그가 ON 으로 바뀌어 선택지에서 사라진 MUSINSA_USED 가 선택된 채 남으면 MUSINSA 로 되돌린다.
  useEffect(() => {
    if (isUsedBundleEnabled && shipperCode === MUSINSA_USED_SHIPPER_CODE) {
      setShipperCode(MUSINSA_SHIPPER_CODE)
    }
  }, [isUsedBundleEnabled, shipperCode])

  const { data } = useWaitVolumePrediction({ shipperCode, cutType })

  const slots = data?.slots ?? []
  // BE는 24h 슬롯을 모두 내려주고, 화면은 현재 시각 ~ 현재+MAX_WAIT_TIME(분) 윈도우만 노출.
  // 표시용 HH:mm 라벨도 같이 만들어 JSX 렌더 시 재파싱 방지.
  const visibleSlots = useMemo(() => {
    if (!slots.length) return []
    const now = new Date()
    const maxStart = new Date(now.getTime() + MAX_WAIT_TIME * 60 * 1000)
    return slots.flatMap((s) => {
      const start = parseISO(s.slotStart)
      const end = parseISO(s.slotEnd)
      if (end <= now || start >= maxStart) return []
      return [{ ...s, startLabel: format(start, 'HH:mm'), endLabel: format(end, 'HH:mm') }]
    })
  }, [slots])
  const total = useMemo(() => visibleSlots.reduce((sum, s) => sum + s.predictedCount, 0), [visibleSlots])

  return (
    <SummaryWrapper>
      <SummaryContainer>
        <SummaryHeader>
          <Text kind="Headline_16_bold">출고지시 예상 주문 수량</Text>
        </SummaryHeader>
        <SelectBoxWrap>
          <SelectBox<string> size="small" options={shipperOptions} value={shipperCode} placeholder={false} onChange={setShipperCode} block />
        </SelectBoxWrap>

        <SlotItemsContainer>
          {visibleSlots.map((slot) => (
            <SlotItem key={`time-slot-${slot.slotStart}-${slot.slotEnd}`}>
              <SlotTimeLabel>
                {slot.startLabel} ~ {slot.endLabel}
              </SlotTimeLabel>
              <OrderCountDisplay>
                <Text kind="Body_14_bold" color={colors.gray900}>
                  {Utils.getCommaNum(slot.predictedCount) || 0}
                </Text>
              </OrderCountDisplay>
            </SlotItem>
          ))}
        </SlotItemsContainer>
        <DividerLine />
        <TotalSummary>
          <Text kind="Body_14_regular" color={colors.gray900}>
            총합
          </Text>
          <OrderCountDisplay>
            <Text kind="Body_14_bold" color={colors.gray900}>
              {Utils.getCommaNum(total) || 0}
            </Text>
          </OrderCountDisplay>
        </TotalSummary>
      </SummaryContainer>
    </SummaryWrapper>
  )
}

const SummaryWrapper = styled.div`
  height: 650px;
`

const SummaryContainer = styled.div`
  width: 280px;
  height: fit-content;
  max-height: 650px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid ${colorSystem.border.table};
`

const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const SelectBoxWrap = styled.div`
  width: 100%;
`

const SlotItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  min-height: 0;
`

const SlotItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${colors.gray050};
`

const SlotTimeLabel = styled.span`
  font-family:
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    'Helvetica Neue',
    Arial,
    sans-serif;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: ${colors.gray900};
  font-variant-numeric: tabular-nums;
`

const OrderCountDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const DividerLine = styled.div`
  width: 100%;
  height: 1px;
  background: ${colorSystem.border.pinnedTable};
`

const TotalSummary = styled.div`
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export default OutboundWaitingByTimeSlotPanel
